import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { dashboardTourSteps } from './tour-steps';
import { useSidebar } from '@/hooks/use-sidebar';
import { useLocation } from 'wouter';

interface TourContextType {
  startTour: () => void;
  stopTour: () => void;
  isRunning: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const [run, setRun] = useState(false);
  const [steps] = useState<Step[]>(dashboardTourSteps);
  const [stepIndex, setStepIndex] = useState(0);
  const [resumeStepIndex, setResumeStepIndex] = useState<number | null>(null);
  const [isAwaitingNavigation, setIsAwaitingNavigation] = useState(false);
  const { setCollapsed, lockSidebarOpen, unlockSidebar, isLockedOpen } = useSidebar();
  const [location, navigate] = useLocation();
  const lastStepIndex = steps.length - 1;

  const getTargetElement = useCallback((target: Step['target']) => {
    if (typeof target === 'string') {
      return document.querySelector(target);
    }

    if (typeof target === 'function') {
      return target();
    }

    return target;
  }, []);

  const clampStepIndex = (value: number) => {
    if (value < 0) {
      return 0;
    }

    if (value > lastStepIndex) {
      return lastStepIndex;
    }

    return value;
  };

  const startTour = () => {
    setStepIndex(0);
    setResumeStepIndex(null);
    setIsAwaitingNavigation(false);

    // Wait for the first target element to be available before starting
    const firstStep = steps[0];
    if (firstStep) {
      let attempts = 0;
      const maxAttempts = 20;

      const checkAndStart = () => {
        attempts += 1;
        const targetElement = getTargetElement(firstStep.target);

        // Validate that element is actually in the DOM and attached
        const isElementValid =
          targetElement && targetElement.isConnected && document.body.contains(targetElement);

        if (isElementValid) {
          setRun(true);
        } else if (attempts < maxAttempts) {
          setTimeout(checkAndStart, 200);
        } else {
          // Don't start if target is not valid after waiting
          console.warn('Tour: First target not found or not attached to DOM, tour will not start');
        }
      };

      checkAndStart();
    } else {
      setRun(true);
    }
  };

  const stopTour = () => {
    setRun(false);
    setStepIndex(0);
    setResumeStepIndex(null);
    setIsAwaitingNavigation(false);
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    const errorStatuses: string[] = [STATUS.ERROR];

    if (finishedStatuses.includes(status)) {
      // Completely stop the tour and cleanup
      setRun(false);
      setStepIndex(0);
      setResumeStepIndex(null);
      setIsAwaitingNavigation(false);

      // Unlock sidebar when tour finishes
      if (isLockedOpen) {
        unlockSidebar();
      }
      return;
    }

    // Handle errors gracefully - refresh the page to recover from detached DOM nodes
    if (errorStatuses.includes(status)) {
      console.warn('Tour encountered an error, refreshing page to recover');
      setRun(false);
      setStepIndex(0);
      setResumeStepIndex(null);
      setIsAwaitingNavigation(false);
      if (isLockedOpen) {
        unlockSidebar();
      }
      // Refresh the page after a brief delay to clean up any detached nodes
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return;
    }

    const advancedToNextStepFromProfile =
      type === EVENTS.STEP_AFTER && action === 'next' && index === 6;

    if (advancedToNextStepFromProfile) {
      setResumeStepIndex(index + 1);
      setIsAwaitingNavigation(true);
      setRun(false);
      navigate('/accounts');
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === 'next' || action === 'skip') {
        setStepIndex(clampStepIndex(index + 1));
      }
      if (action === 'prev') {
        setStepIndex(clampStepIndex(index - 1));
      }
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn(`Tour: Target not found for step ${index}, attempting to continue`);
      if (action === 'next' || action === 'skip') {
        setStepIndex(clampStepIndex(index + 1));
      }
      if (action === 'prev') {
        setStepIndex(clampStepIndex(index - 1));
      }
    }
  };

  useEffect(() => {
    if (isAwaitingNavigation && location === '/accounts' && resumeStepIndex !== null) {
      let isCancelled = false;
      let timeoutId: number | null = null;
      const stepToResume = steps[resumeStepIndex];
      let attempts = 0;
      const maxAttempts = 30;

      const finishResume = () => {
        if (isCancelled) {
          return;
        }

        setStepIndex(resumeStepIndex);
        setRun(true);
        setResumeStepIndex(null);
        setIsAwaitingNavigation(false);
      };

      const tryResume = () => {
        if (isCancelled) {
          return;
        }

        attempts += 1;

        if (stepToResume) {
          const targetElement = getTargetElement(stepToResume.target);
          // Validate that element is actually in the DOM and attached
          const isElementValid =
            targetElement && targetElement.isConnected && document.body.contains(targetElement);

          if (isElementValid) {
            finishResume();
            return;
          }
        }

        if (attempts >= maxAttempts) {
          // Don't resume if target is never valid - refresh to recover
          console.warn('Tour: Resume target not found or not attached to DOM, refreshing page');
          setRun(false);
          setResumeStepIndex(null);
          setIsAwaitingNavigation(false);
          // Refresh to clean up any DOM issues
          setTimeout(() => {
            window.location.reload();
          }, 500);
          return;
        }

        timeoutId = window.setTimeout(tryResume, 100);
      };

      tryResume();

      return () => {
        isCancelled = true;
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      };
    }
  }, [getTargetElement, isAwaitingNavigation, location, resumeStepIndex, steps]);

  useEffect(() => {
    if (run || isAwaitingNavigation) {
      if (!isLockedOpen) {
        lockSidebarOpen();
      }
      setCollapsed(false);
    } else if (isLockedOpen) {
      unlockSidebar();
    }
  }, [isAwaitingNavigation, isLockedOpen, lockSidebarOpen, run, setCollapsed, unlockSidebar]);

  return (
    <TourContext.Provider value={{ startTour, stopTour, isRunning: run || isAwaitingNavigation }}>
      {children}
      {run && (
        <Joyride
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          hideCloseButton
          disableCloseOnEsc
          disableOverlayClose
          scrollToFirstStep={false}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              primaryColor: '#1e3a8a',
              zIndex: 99999,
              arrowColor: '#ffffff',
              backgroundColor: '#ffffff',
            },
            buttonNext: {
              backgroundColor: '#1e3a8a',
              fontSize: 14,
              padding: '8px 16px',
            },
            buttonBack: {
              color: '#6b7280',
              fontSize: 14,
            },
            buttonSkip: {
              color: '#6b7280',
              fontSize: 14,
            },
            tooltipContainer: {
              maxWidth: 320,
              textAlign: 'left',
            },
            tooltip: {
              zIndex: 99999,
            },
            overlay: {
              zIndex: 99998,
            },
            beacon: {
              display: 'none',
            },
            beaconInner: {
              display: 'none',
            },
            beaconOuter: {
              display: 'none',
            },
          }}
          floaterProps={{
            offset: 16,
            disableFlip: false,
            placement: 'top-start',
            hideArrow: false,
            options: {
              // Prevent errors when elements are detached from DOM
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'clippingParents',
                  },
                },
                {
                  name: 'computeStyles',
                  options: {
                    // Add guards to prevent null reference errors
                    gpuAcceleration: false,
                  },
                },
              ],
            },
          }}
          disableScrolling={true}
          disableScrollParentFix
          disableBeacon
          scrollOffset={120}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
